import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BedDouble, Trees, Armchair, Coffee, KeyRound, Phone, ChevronDown, Play } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { RoomCard } from "../components/RoomCard";
import { CTABand } from "../components/CTABand";
import { useLang } from "../i18n";

const HERO_LINES = ["Le dolci", "Dimore"];

const SERVICE_ICONS = {
  bath: BedDouble,
  trees: Trees,
  armchair: Armchair,
  phone: Phone,
  coffee: Coffee,
  "key-round": KeyRound,
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const [reducedMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const { l, data, t } = useLang();
  const { SITE, IMAGES } = data;
  const h = t.home.hero;

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        {reducedMotion ? (
          <img
            src={IMAGES.villaHero}
            alt={h.alt}
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
        ) : (
          <motion.video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/images/video-villa-drone-poster.jpg"
            aria-hidden="true"
            className="w-full h-full object-cover"
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ scale }}
          >
            <source src="/video/hero-loop.mp4" type="video/mp4" />
          </motion.video>
        )}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/40 to-ink/15" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10 pb-28 md:pb-14 pt-40 w-full flex-1 flex flex-col justify-end">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="text-[11px] md:text-xs uppercase tracking-[0.3em] text-cream/85 mb-5"
        >
          {h.eyebrow}
        </motion.p>
        <h1 className="font-serif text-[19vw] sm:text-8xl lg:text-[9.5rem] leading-[0.95] text-cream [text-shadow:0_2px_30px_rgba(0,0,0,0.4)]">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="masked-line pb-[0.09em] -mb-[0.09em]">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.18, duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
                className={i === 1 ? "italic" : ""}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="max-w-md text-sm md:text-base text-cream/85 leading-relaxed"
          >
            {h.paragraph}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link
              to={l("/camere")}
              data-testid="hero-rooms-button"
              className="inline-flex items-center rounded-full border border-cream/50 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-cream transition-colors duration-300 hover:bg-cream/15"
            >
              {h.ctaRooms}
            </Link>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-booking-button"
              className="inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-cream transition-[background-color,transform] duration-300 hover:bg-terra-dark hover:-translate-y-0.5"
            >
              {h.ctaBook} <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-cream/70"
        aria-hidden="true"
      >
        <ChevronDown className="w-6 h-6 animate-bounce" />
      </motion.div>
    </section>
  );
};

const VideoSection = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const { t } = useLang();
  const v = t.home.video;
  const play = () => {
    videoRef.current?.play();
    setPlaying(true);
  };
  return (
    <section data-testid="video-section" className="max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-4">
        <Reveal>
          <p className="eyebrow mb-5">{v.eyebrow}</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight text-ink">
            {v.title}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base md:text-lg text-body leading-relaxed">
            {v.text}
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.12} className="md:col-span-8">
        <div className="relative img-frame aspect-video bg-ink">
          <video
            ref={videoRef}
            controls={playing}
            preload="none"
            playsInline
            poster="/images/video-villa-drone-poster.jpg"
            className="w-full h-full object-cover"
            aria-label={v.videoLabel}
          >
            <source src="/video/villa-dal-drone.mp4" type="video/mp4" />
          </video>
          {!playing && (
            <button
              type="button"
              onClick={play}
              data-testid="video-play-button"
              aria-label={v.play}
              className="absolute inset-0 flex items-center justify-center group"
            >
              <span className="w-20 h-20 rounded-full bg-cream/90 flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                <Play className="w-7 h-7 text-sage ml-1" fill="currentColor" />
              </span>
            </button>
          )}
        </div>
      </Reveal>
    </section>
  );
};

export default function Home() {
  const { l, data, t } = useLang();
  const { IMAGES, ROOMS, SERVICES, PRACTICAL_INFO } = data;
  const h = t.home;

  return (
    <>
      <Seo title={h.seo.title} description={h.seo.description} path="/" />
      <Hero />
      <Marquee />

      {/* Presentazione */}
      <section data-testid="intro-section" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
        <Reveal className="md:col-span-5">
          <div className="img-frame aspect-[4/5]">
            <img src={IMAGES.facade} alt={h.intro.alt} loading="lazy" className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div className="md:col-span-6 md:col-start-7">
          <Reveal>
            <p className="eyebrow mb-5">{h.intro.eyebrow}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-ink">
              {h.intro.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg text-body leading-relaxed">
              {h.intro.p1}
            </p>
            <p className="mt-4 text-base md:text-lg text-body leading-relaxed">
              {h.intro.p2}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <Link
              to={l("/la-struttura")}
              data-testid="intro-structure-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-sage border-b border-sage/40 pb-1 transition-colors duration-300 hover:text-terra hover:border-terra"
            >
              {h.intro.link} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <VideoSection />

      {/* Camere */}
      <section data-testid="rooms-section" className="bg-surface border-y border-sand">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
            <Reveal className="md:col-span-7">
              <p className="eyebrow mb-5">{h.rooms.eyebrow}</p>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight text-ink max-w-xl">
                {h.rooms.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-4 md:col-start-9">
              <p className="text-sm md:text-base text-body leading-relaxed">
                {h.rooms.text}
              </p>
              <Link
                to={l("/camere")}
                data-testid="rooms-section-all-link"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-sage hover:text-terra transition-colors duration-300"
              >
                {h.rooms.link} <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <Reveal className="md:col-span-7">
              <RoomCard room={ROOMS[0]} />
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-5">
              <RoomCard room={ROOMS[1]} tall />
            </Reveal>
            <Reveal className="md:col-span-5">
              <RoomCard room={ROOMS[2]} tall />
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-7">
              <RoomCard room={ROOMS[3]} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Servizi */}
      <section data-testid="services-section" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-32">
        <Reveal>
          <p className="eyebrow mb-5">{h.services.eyebrow}</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight text-ink max-w-2xl">
            {h.services.title}
          </h2>
        </Reveal>
        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-sand border border-sand">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[s.icon];
            return (
              <Reveal key={s.title} delay={0.06 * i} className="bg-cream">
                <div className="p-8 md:p-10 h-full">
                  <Icon className="w-6 h-6 text-terra" aria-hidden="true" />
                  <h3 className="font-serif text-xl md:text-2xl mt-5 text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm text-body leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.15}>
          <Link
            to={l("/servizi-e-informazioni")}
            data-testid="services-section-link"
            className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-sage border-b border-sage/40 pb-1 transition-colors duration-300 hover:text-terra hover:border-terra"
          >
            {h.services.link} <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      {/* Territorio teaser */}
      <section data-testid="territory-section" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.mantova} alt={h.territory.alt} loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12">
          <Reveal className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-cream/70 mb-5">{h.territory.eyebrow}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-cream max-w-xl">
              {h.territory.title}
            </h2>
            <p className="mt-6 text-base md:text-lg text-cream/80 leading-relaxed max-w-xl">
              {h.territory.text}
            </p>
            <Link
              to={l("/il-territorio")}
              data-testid="territory-section-link"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-cream px-7 py-3.5 text-sm font-semibold text-ink transition-[background-color,transform] duration-300 hover:bg-sand hover:-translate-y-0.5"
            >
              {h.territory.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Turismo / Lavoro */}
      <section data-testid="audience-section" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-8">
        <Reveal>
          <div className="img-frame h-full flex flex-col">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={IMAGES.gardenSeating} alt={h.audience.tourism.alt} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-10">
              <p className="eyebrow mb-4">{h.audience.tourism.eyebrow}</p>
              <h3 className="font-serif text-2xl md:text-3xl text-ink">{h.audience.tourism.title}</h3>
              <p className="mt-4 text-sm md:text-base text-body leading-relaxed">
                {h.audience.tourism.text}
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="img-frame h-full flex flex-col">
            <div className="aspect-[16/10] overflow-hidden">
              <img src="/images/camera-104-vista-le-dolci-dimore.jpg" alt={h.audience.business.alt} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-10">
              <p className="eyebrow mb-4">{h.audience.business.eyebrow}</p>
              <h3 className="font-serif text-2xl md:text-3xl text-ink">{h.audience.business.title}</h3>
              <p className="mt-4 text-sm md:text-base text-body leading-relaxed">
                {h.audience.business.text}
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Info pratiche */}
      <section data-testid="practical-info-section" className="border-t border-sand">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <p className="eyebrow mb-10">{h.practical.eyebrow}</p>
          </Reveal>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
            {PRACTICAL_INFO.map((info, i) => (
              <Reveal key={info.label} delay={0.05 * i}>
                <div className="border-l-2 border-terra/50 pl-5">
                  <dt className="text-xs uppercase tracking-[0.18em] text-body">{info.label}</dt>
                  <dd className="mt-2 font-serif text-xl text-ink">{info.value}</dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      <CTABand />
    </>
  );
}
